import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { captureException, track } from '@turfchale/analytics'
import { supabase, useAuthStore } from '@turfchale/supabase'
import { format, isPast, isToday, isTomorrow, parseISO } from 'date-fns'
import {
  Calendar,
  Clock,
  CreditCard,
  LogOut,
  MapPin,
  QrCode,
  Ticket,
  TrendingUp,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import toast from 'react-hot-toast'

import { Logo } from '@/components/ui/Logo'

export const Route = createFileRoute('/app/')({
  component: PlayerDashboard,
})

// ─── Types ──────────────────────────────────────────────────────────

interface Booking {
  amount: number
  cancellation_reason: null | string
  cancelled_at: null | string
  confirmation_code: null | string
  created_at: string
  deposit_amount: number
  id: string
  notes: null | string
  payment_status: PaymentStatus
  platform_fee: number
  player_id: string
  razorpay_order_id: null | string
  razorpay_payment_id: null | string
  reviews: Array<{ comment: null | string; id: string; rating: number }> | null
  slot_date: string
  slot_end_time: string
  slot_id: string
  slot_start_time: string
  status: BookingStatus
  total_amount: number
  turf_address: string
  turf_id: string
  turf_name: string
}

type BookingStatus = 'cancelled' | 'completed' | 'confirmed' | 'no_show' | 'pending'
type PaymentStatus = 'failed' | 'paid' | 'pending' | 'refunded'

// ─── Sub-components ─────────────────────────────────────────────────

function BookingCard({ booking }: { booking: Booking }) {
  const [activePanel, setActivePanel] = useState<'cancel' | 'review' | null>(null)
  const existingReview = booking.reviews?.[0]
  const bookingDate = parseISO(booking.slot_date)

  let dateLabel = format(bookingDate, 'MMM d, yyyy')
  if (isToday(bookingDate)) dateLabel = 'Today'
  else if (isTomorrow(bookingDate)) dateLabel = 'Tomorrow'

  return (
    <div
      style={{
        background: 'var(--bg2)',
        border: '1px solid var(--lime-line)',
        borderRadius: 12,
        overflow: 'hidden',
        transition: 'border-color 0.15s',
      }}
    >
      <div style={{ padding: '16px 20px' }}>
        {/* Top row: turf name + status badge */}
        <div
          style={{
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'Clash Display',
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 2,
              }}
            >
              {booking.turf_name}
            </div>
            <div
              style={{
                alignItems: 'center',
                color: 'var(--muted)',
                display: 'flex',
                fontSize: 12,
                gap: 4,
              }}
            >
              <MapPin size={12} />
              {booking.turf_address}
            </div>
          </div>
          <span
            style={{
              background: `${statusColor(booking.status)}15`,
              borderRadius: 6,
              color: statusColor(booking.status),
              fontSize: 11,
              fontWeight: 600,
              padding: '4px 10px',
              whiteSpace: 'nowrap',
            }}
          >
            {statusLabel(booking.status)}
          </span>
        </div>

        {/* Middle: date/time + confirmation code */}
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 14,
          }}
        >
          <InfoChip icon={<Calendar size={13} />} label={dateLabel} />
          <InfoChip
            icon={<Clock size={13} />}
            label={`${formatTime(booking.slot_start_time)} – ${formatTime(booking.slot_end_time)}`}
          />
          {booking.confirmation_code && (
            <InfoChip icon={<QrCode size={13} />} label={`Code: ${booking.confirmation_code}`} />
          )}
        </div>

        {/* Payment row */}
        <div
          style={{
            alignItems: 'center',
            borderTop: '1px solid var(--lime-line)',
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: 12,
          }}
        >
          <div style={{ alignItems: 'center', display: 'flex', gap: 12 }}>
            <div style={{ alignItems: 'center', display: 'flex', gap: 6 }}>
              <CreditCard size={13} style={{ color: 'var(--text-dim)' }} />
              <span
                style={{
                  background: `${paymentColor(booking.payment_status)}15`,
                  borderRadius: 4,
                  color: paymentColor(booking.payment_status),
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 7px',
                }}
              >
                {booking.payment_status.charAt(0).toUpperCase() + booking.payment_status.slice(1)}
              </span>
            </div>
          </div>
          <div style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
            {(booking.status === 'pending' ||
              (booking.status === 'confirmed' && booking.payment_status === 'paid')) && (
              <button
                onClick={() => {
                  const next = activePanel === 'cancel' ? null : 'cancel'
                  if (next === 'cancel') {
                    track('booking_cancel_initiated', { booking_id: booking.id })
                  }
                  setActivePanel(next)
                }}
                style={{
                  background: activePanel === 'cancel' ? '#ef444415' : 'var(--bg3)',
                  border: `1px solid ${activePanel === 'cancel' ? '#ef444430' : 'var(--lime-line)'}`,
                  borderRadius: 7,
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '5px 12px',
                }}
              >
                Cancel
              </button>
            )}
            {booking.status === 'completed' && (
              <button
                onClick={() => {
                  const next = activePanel === 'review' ? null : 'review'
                  if (next === 'review') {
                    track('review_form_opened', {
                      booking_id: booking.id,
                      turf_id: booking.turf_id,
                    })
                  }
                  setActivePanel(next)
                }}
                style={{
                  background: activePanel === 'review' ? 'var(--lime-dim)' : 'var(--bg3)',
                  border: `1px solid ${activePanel === 'review' ? 'var(--lime)' : 'var(--lime-line)'}`,
                  borderRadius: 7,
                  color: existingReview ? 'var(--muted)' : 'var(--lime)',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '5px 12px',
                }}
              >
                {existingReview ? 'Edit Review' : 'Leave a Review'}
              </button>
            )}
            <div
              style={{
                fontFamily: 'Clash Display',
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              {formatCurrency(booking.total_amount)}
            </div>
          </div>
        </div>
      </div>

      {/* Expandable detail row: show more info if present */}
      {(booking.razorpay_payment_id || booking.notes || booking.cancellation_reason) && (
        <div
          style={{
            background: 'var(--bg)',
            borderTop: '1px solid var(--lime-line)',
            fontSize: 12,
            padding: '10px 20px',
          }}
        >
          <div
            style={{
              alignItems: 'center',
              color: 'var(--text-dim)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            {booking.razorpay_payment_id && <span>Payment ID: {booking.razorpay_payment_id}</span>}
            {booking.notes && <span>Note: {booking.notes}</span>}
            {booking.cancellation_reason && (
              <span style={{ color: '#ef4444' }}>Reason: {booking.cancellation_reason}</span>
            )}
          </div>
        </div>
      )}

      {activePanel === 'cancel' && (
        <CancelConfirm
          bookingId={booking.id}
          isPaid={booking.payment_status === 'paid'}
          onClose={() => setActivePanel(null)}
          turfId={booking.turf_id}
        />
      )}
      {activePanel === 'review' && (
        <ReviewForm booking={booking} onClose={() => setActivePanel(null)} />
      )}
    </div>
  )
}

function CancelConfirm({
  bookingId,
  isPaid,
  onClose,
  turfId,
}: {
  bookingId: string
  isPaid: boolean
  onClose: () => void
  turfId: string
}) {
  const profile = useAuthStore((s) => s.profile)
  const queryClient = useQueryClient()
  const { isPending, mutate } = useMutation({
    mutationFn: async () => {
      if (isPaid) {
        const { error } = await supabase.functions.invoke('cancel-booking', {
          body: { booking_id: bookingId },
        })
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('bookings')
          .update({ cancelled_at: new Date().toISOString(), status: 'cancelled' })
          .eq('id', bookingId)
          .eq('status', 'pending')
        if (error) throw error
      }
    },
    onError: (error) => {
      track('booking_cancel_failed', { booking_id: bookingId, reason: error.message })
      captureException(error, { booking_id: bookingId, context: 'booking_cancel' })
      toast.error('Failed to cancel booking')
    },
    onSuccess: () => {
      track('booking_cancelled', { booking_id: bookingId, turf_id: turfId })
      queryClient.invalidateQueries({ queryKey: ['player-bookings', profile?.id] })
      toast.success('Booking cancelled')
      onClose()
    },
  })

  return (
    <div
      style={{
        background: 'var(--bg)',
        borderTop: '1px solid var(--lime-line)',
        padding: '12px 20px',
      }}
    >
      <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 12px' }}>
        {isPaid
          ? "Cancel this booking? Your payment will be refunded. This can't be undone."
          : "Cancel this booking? This can't be undone."}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          disabled={isPending}
          onClick={onClose}
          style={{
            background: 'var(--bg3)',
            border: '1px solid var(--lime-line)',
            borderRadius: 8,
            color: 'var(--text-dim)',
            cursor: isPending ? 'not-allowed' : 'pointer',
            fontSize: 13,
            padding: '8px 16px',
          }}
        >
          Go Back
        </button>
        <button
          disabled={isPending}
          onClick={() => mutate()}
          style={{
            background: '#ef444415',
            border: '1px solid #ef444430',
            borderRadius: 8,
            color: '#ef4444',
            cursor: isPending ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            opacity: isPending ? 0.6 : 1,
            padding: '8px 16px',
          }}
        >
          {isPending ? 'Cancelling…' : 'Yes, Cancel'}
        </button>
      </div>
    </div>
  )
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    currency: 'INR',
    maximumFractionDigits: 0,
    style: 'currency',
  }).format(amount)
}

function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`
}

function InfoChip({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div
      style={{
        alignItems: 'center',
        color: 'var(--muted)',
        display: 'flex',
        fontSize: 12,
        gap: 4,
      }}
    >
      {icon}
      {label}
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────

function paymentColor(status: PaymentStatus): string {
  switch (status) {
    case 'failed':
      return '#ef4444'
    case 'paid':
      return '#b8ec3c'
    case 'pending':
      return '#fbbf24'
    case 'refunded':
      return '#60a5fa'
  }
}

function PlayerDashboard() {
  const { profile, signOut } = useAuthStore()
  const navigate = useNavigate()
  const { data: bookings = [], isLoading } = useBookings()

  const upcoming = bookings.filter(
    (b) =>
      (b.status === 'confirmed' || b.status === 'pending') &&
      !isPast(parseISO(`${b.slot_date}T${b.slot_end_time}`)),
  )

  const past = bookings.filter(
    (b) =>
      b.status === 'cancelled' ||
      b.status === 'completed' ||
      b.status === 'no_show' ||
      isPast(parseISO(`${b.slot_date}T${b.slot_end_time}`)),
  )

  const completedCount = bookings.filter((b) => b.status === 'completed').length
  const totalBookings = bookings.length

  const handleSignOut = async () => {
    track('auth_signed_out', { role: profile?.role ?? 'player' })
    await signOut()
    navigate({ to: '/' })
    toast.success('Signed out')
  }

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>
      {/* Header */}
      <header
        style={{
          alignItems: 'center',
          background: 'var(--bg2)',
          borderBottom: '1px solid var(--lime-line)',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '16px 24px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', gap: 12 }}>
          <Logo fontSize={20} />
          <div style={{ borderLeft: '1px solid var(--lime-line)', paddingLeft: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>My Dashboard</div>
            <div style={{ color: 'var(--muted)', fontSize: 12 }}>
              {profile?.full_name ?? profile?.email}
            </div>
          </div>
        </div>
        <div style={{ alignItems: 'center', display: 'flex', gap: 12 }}>
          <button
            onClick={handleSignOut}
            style={{
              alignItems: 'center',
              background: 'var(--bg3)',
              border: '1px solid var(--lime-line)',
              borderRadius: 8,
              color: 'var(--muted)',
              cursor: 'pointer',
              display: 'flex',
              fontSize: 13,
              gap: 6,
              padding: '8px 14px',
            }}
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>
      </header>

      <div style={{ margin: '0 auto', maxWidth: 960, padding: '24px 16px' }}>
        {/* Stats Row */}
        <div
          style={{
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            marginBottom: 32,
          }}
        >
          <StatCard
            icon={<Ticket size={18} />}
            label="Total Bookings"
            value={totalBookings.toString()}
          />
          <StatCard
            icon={<TrendingUp size={18} />}
            label="Completed"
            value={completedCount.toString()}
          />
          <StatCard
            icon={<Calendar size={18} />}
            label="Upcoming"
            value={upcoming.length.toString()}
          />
        </div>

        {/* Loading state */}
        {isLoading && (
          <div
            style={{
              color: 'var(--muted)',
              fontSize: 14,
              padding: 48,
              textAlign: 'center',
            }}
          >
            Loading your bookings…
          </div>
        )}

        {!isLoading && bookings.length === 0 && (
          <div
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--lime-line)',
              borderRadius: 12,
              padding: 48,
              textAlign: 'center',
            }}
          >
            <Ticket size={40} style={{ color: 'var(--text-dim)', marginBottom: 12 }} />
            <div
              style={{
                fontFamily: 'Clash Display',
                fontSize: 18,
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              No bookings yet
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 20 }}>
              Head to turfchale.com to book your first turf
            </div>
            <Link
              style={{
                background: 'var(--lime)',
                border: 'none',
                borderRadius: 10,
                color: '#0b1c10',
                display: 'inline-block',
                fontSize: 14,
                fontWeight: 600,
                padding: '10px 20px',
                textDecoration: 'none',
              }}
              to="/turfs"
            >
              Browse Turfs
            </Link>
          </div>
        )}

        {!isLoading && bookings.length > 0 && (
          <>
            {/* Upcoming */}
            {upcoming.length > 0 && (
              <section style={{ marginBottom: 40 }}>
                <SectionTitle
                  count={upcoming.length}
                  icon={<Calendar size={16} />}
                  title="Upcoming"
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {upcoming.map((booking) => (
                    <BookingCard booking={booking} key={booking.id} />
                  ))}
                </div>
              </section>
            )}

            {/* Past */}
            {past.length > 0 && (
              <section style={{ marginBottom: 40 }}>
                <SectionTitle
                  count={past.length}
                  icon={<Clock size={16} />}
                  title="Past Bookings"
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {past.map((booking) => (
                    <BookingCard booking={booking} key={booking.id} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ReviewForm({ booking, onClose }: { booking: Booking; onClose: () => void }) {
  const profile = useAuthStore((s) => s.profile)
  const queryClient = useQueryClient()
  const existingReview = booking.reviews?.[0]
  const [rating, setRating] = useState(existingReview?.rating ?? 0)
  const [comment, setComment] = useState(existingReview?.comment ?? '')
  const [hovered, setHovered] = useState(0)

  const { isPending, mutate } = useMutation({
    mutationFn: async () => {
      if (existingReview) {
        const { error } = await supabase
          .from('reviews')
          .update({ comment: comment || null, rating })
          .eq('id', existingReview.id)
        if (error) throw error
      } else {
        if (!profile) throw new Error('Not authenticated')
        const { error } = await supabase.from('reviews').insert({
          booking_id: booking.id,
          comment: comment || null,
          player_id: profile.id,
          rating,
          turf_id: booking.turf_id,
        })
        if (error) throw error
      }
    },
    onError: (error) => {
      track('review_submission_failed', { error: error.message, turf_id: booking.turf_id })
      captureException(error, { context: 'review_submit', turf_id: booking.turf_id })
      toast.error('Could not submit review')
    },
    onSuccess: () => {
      track('review_submitted', { booking_id: booking.id, rating, turf_id: booking.turf_id })
      queryClient.invalidateQueries({ queryKey: ['player-bookings', profile?.id] })
      toast.success(existingReview ? 'Review updated!' : 'Review submitted!')
      onClose()
    },
  })

  const displayRating = hovered || rating

  return (
    <div
      style={{
        background: 'var(--bg)',
        borderTop: '1px solid var(--lime-line)',
        padding: '16px 20px',
      }}
    >
      <div style={{ display: 'flex', gap: 2, marginBottom: 12 }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => setRating(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            style={{
              background: 'none',
              border: 'none',
              color: star <= displayRating ? 'var(--lime)' : 'var(--bg3)',
              cursor: 'pointer',
              fontSize: 28,
              lineHeight: 1,
              padding: '0 2px',
            }}
          >
            ★
          </button>
        ))}
      </div>
      <textarea
        onChange={(e) => setComment(e.target.value)}
        placeholder="Add a comment (optional)"
        rows={3}
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--lime-line)',
          borderRadius: 8,
          boxSizing: 'border-box',
          color: 'var(--text)',
          display: 'block',
          fontSize: 13,
          marginBottom: 12,
          padding: '8px 12px',
          resize: 'vertical',
          width: '100%',
        }}
        value={comment ?? ''}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          disabled={isPending}
          onClick={onClose}
          style={{
            background: 'var(--bg3)',
            border: '1px solid var(--lime-line)',
            borderRadius: 8,
            color: 'var(--text-dim)',
            cursor: isPending ? 'not-allowed' : 'pointer',
            fontSize: 13,
            padding: '8px 16px',
          }}
        >
          Cancel
        </button>
        <button
          disabled={isPending || rating === 0}
          onClick={() => mutate()}
          style={{
            background: 'var(--lime)',
            border: 'none',
            borderRadius: 8,
            color: '#0b1c10',
            cursor: isPending || rating === 0 ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            opacity: isPending || rating === 0 ? 0.5 : 1,
            padding: '8px 16px',
          }}
        >
          {isPending ? 'Saving…' : existingReview ? 'Update Review' : 'Submit Review'}
        </button>
      </div>
    </div>
  )
}

function SectionTitle({ count, icon, title }: { count: number; icon: ReactNode; title: string }) {
  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: 8,
        marginBottom: 16,
      }}
    >
      <div style={{ color: 'var(--lime)' }}>{icon}</div>
      <h2
        style={{
          fontFamily: 'Clash Display',
          fontSize: 18,
          fontWeight: 600,
          margin: 0,
        }}
      >
        {title}
      </h2>
      <span
        style={{
          background: 'var(--lime-dim)',
          borderRadius: 999,
          color: 'var(--lime)',
          fontSize: 11,
          fontWeight: 600,
          padding: '2px 8px',
        }}
      >
        {count}
      </span>
    </div>
  )
}

// ─── Queries ────────────────────────────────────────────────────────

function StatCard({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div
      style={{
        background: 'var(--bg2)',
        border: '1px solid var(--lime-line)',
        borderRadius: 12,
        padding: '16px 20px',
      }}
    >
      <div style={{ color: 'var(--lime)', marginBottom: 6 }}>{icon}</div>
      <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 2 }}>{label}</div>
      <div
        style={{
          color: 'var(--text)',
          fontFamily: 'Clash Display',
          fontSize: 22,
          fontWeight: 700,
        }}
      >
        {value}
      </div>
    </div>
  )
}

// ─── Main Component ─────────────────────────────────────────────────

function statusColor(status: BookingStatus): string {
  switch (status) {
    case 'cancelled':
      return '#ef4444'
    case 'completed':
      return '#60a5fa'
    case 'confirmed':
      return '#b8ec3c'
    case 'no_show':
      return '#f97316'
    case 'pending':
      return '#fbbf24'
  }
}

function statusLabel(status: BookingStatus): string {
  switch (status) {
    case 'cancelled':
      return 'Cancelled'
    case 'completed':
      return 'Completed'
    case 'confirmed':
      return 'Confirmed'
    case 'no_show':
      return 'No Show'
    case 'pending':
      return 'Pending'
  }
}

function useBookings() {
  const profile = useAuthStore((s) => s.profile)

  return useQuery({
    enabled: !!profile,
    queryFn: async () => {
      if (!profile) return []
      const { data, error } = await supabase
        .from('bookings')
        .select('*, reviews(id, rating, comment)')
        .eq('player_id', profile.id)
        .order('slot_date', { ascending: false })
        .order('slot_start_time', { ascending: false })

      if (error) throw error
      return data as Booking[]
    },
    queryKey: ['player-bookings', profile?.id],
    refetchOnMount: true,
  })
}
